import moment from 'moment';
import {
  defaultIntervalFrom,
  defaultIntervalTo,
  intervalDateFormat,
} from '../constants';
import { getScheduleMonth } from '../../../model/workSchedulePeriod';

export const getShiftType = (shiftType, durationHours) => {
  if (durationHours >= 24) {
    return 'daily';
  }

  if (
    shiftType === 'daily' ||
    shiftType === 'fullDay' ||
    shiftType === 'roundTheClock'
  ) {
    return 'daily';
  }

  return shiftType;
};

export const getScheduleStartMonth = (schedule) => {
  return getScheduleMonth(schedule, 'startMonth', 'startDate');
};

export const getSchedulePeriodText = (schedule) => {
  const startMonth = getScheduleStartMonth(schedule);
  const endMonth = getScheduleMonth(schedule, 'endMonth', 'endDate');

  if (!startMonth) {
    return '';
  }

  if (!endMonth || startMonth.isSame(endMonth, 'month')) {
    return startMonth.format('MMMM YYYY');
  }

  return `${startMonth.format('MM.YYYY')} - ${endMonth.format('MM.YYYY')}`;
};

export const getPeriodDays = (schedule) => {
  const startMonth = getScheduleStartMonth(schedule);
  const endMonth = getScheduleMonth(schedule, 'endMonth', 'endDate');

  if (!startMonth || !endMonth || startMonth.isAfter(endMonth, 'month')) {
    return [];
  }

  const days = [];
  const startDate = startMonth.clone().startOf('month');
  const endDate = endMonth.clone().endOf('month');

  for (
    const date = startDate.clone();
    date.isSameOrBefore(endDate, 'day');
    date.add(1, 'day')
  ) {
    days.push(date.clone());
  }

  return days;
};

const getWorkerIntervalEntries = (schedule, workerId) => {
  const workerIntervals = schedule?.workIntervals?.[String(workerId)];

  if (!workerIntervals) {
    return [];
  }

  return Object.entries(workerIntervals)
    .map(([date, interval]) => {
      const parsedDate = moment(date, intervalDateFormat, true);

      return { date, parsedDate, interval };
    })
    .filter(
      ({ parsedDate, interval }) =>
        parsedDate.isValid() && interval?.from && interval?.to
    )
    .sort((first, second) => first.parsedDate.diff(second.parsedDate));
};

export const getIntervalAssignmentControls = (schedule, workerId) => {
  const intervalEntries = getWorkerIntervalEntries(schedule, workerId);

  if (!intervalEntries.length) {
    return null;
  }

  const intervalGroups = intervalEntries.reduce((groups, item) => {
    const key = `${item.interval.from}-${item.interval.to}`;

    if (!groups.has(key)) {
      groups.set(key, {
        from: item.interval.from,
        to: item.interval.to,
        weekdays: new Set(),
        count: 0,
      });
    }

    const group = groups.get(key);

    group.weekdays.add(item.parsedDate.isoWeekday());
    group.count += 1;

    return groups;
  }, new Map());
  const [selectedGroup] = Array.from(intervalGroups.values()).sort(
    (first, second) => second.count - first.count
  );

  if (!selectedGroup) {
    return null;
  }

  return {
    from: selectedGroup.from,
    to: selectedGroup.to,
    weekdays: Array.from(selectedGroup.weekdays).sort(
      (first, second) => first - second
    ),
  };
};

const isValidWorkInterval = (interval) => interval?.from && interval?.to;

export const getScheduleWorkIntervalCount = (schedule) =>
  Object.values(schedule?.workIntervals || {}).reduce(
    (totalCount, intervalsByDate = {}) =>
      totalCount +
      Object.values(intervalsByDate).filter(isValidWorkInterval).length,
    0
  );

export const getScheduleWorkIntervalDateKeys = (schedule) =>
  Array.from(
    Object.values(schedule?.workIntervals || {}).reduce(
      (dateKeys, intervalsByDate = {}) => {
        Object.entries(intervalsByDate).forEach(([date, interval]) => {
          if (isValidWorkInterval(interval)) {
            dateKeys.add(date);
          }
        });

        return dateKeys;
      },
      new Set()
    )
  );

export const getWorkerWorkIntervalDateKeys = (schedule, workerId) =>
  Object.entries(schedule?.workIntervals?.[String(workerId)] || {})
    .filter(([, interval]) => isValidWorkInterval(interval))
    .map(([date]) => date);

export const getIntervalAssignmentSettingsKey = ({
  from,
  repeatMode,
  to,
  weekdays,
}) =>
  JSON.stringify({
    from: from || defaultIntervalFrom,
    repeatMode,
    to: to || defaultIntervalTo,
    weekdays: [...weekdays].sort((first, second) => first - second),
  });

const getArrayValue = (value) =>
  Array.isArray(value)
    ? value
    : [value].filter(
        (item) => item !== undefined && item !== null && item !== ''
      );

const getObjectValue = (object, path) =>
  path.split('.').reduce((value, key) => value?.[key], object);

const getEntityId = (entity, idKeys) => {
  if (entity && typeof entity === 'object') {
    const idKey = idKeys.find(
      (key) => getObjectValue(entity, key) !== undefined
    );

    return idKey ? getObjectValue(entity, idKey) : undefined;
  }

  return entity;
};

const getEntityName = (entity) => {
  if (!entity || typeof entity !== 'object') {
    return '';
  }

  return (
    entity.addressName ||
    entity.fullAddress ||
    entity.fullName ||
    entity.description ||
    entity.text ||
    entity.content ||
    entity.label ||
    entity.cityName ||
    entity.streetName ||
    entity.houseName ||
    entity.name ||
    entity.building ||
    entity.number ||
    ''
  );
};

const getEntityCityId = (entity = {}) => entity.cityId ?? entity.city?.id;

const getEntityStreetId = (entity = {}) =>
  entity.streetId ?? entity.street?.id ?? entity.street?.streetId;

const getHouseName = (house) => {
  if (!house || typeof house !== 'object') {
    return '';
  }

  return (
    house.addressName ||
    house.fullName ||
    house.fullAddress ||
    house.description ||
    house.text ||
    house.content ||
    house.label ||
    house.name ||
    house.houseName ||
    house.building ||
    house.number ||
    ''
  );
};

const createAddressItems = ({
  entities = [],
  ids = [],
  idKeys,
  fallback,
  getName = getEntityName,
}) => {
  const itemsMap = new Map();
  const selectedIds = getArrayValue(ids);
  const selectedIdSet = new Set(selectedIds.map(String));

  getArrayValue(entities).forEach((entity) => {
    const id = getEntityId(entity, idKeys);
    const name = getName(entity);

    if (id === undefined || id === null) {
      return;
    }

    if (selectedIdSet.size && !selectedIdSet.has(String(id))) {
      return;
    }

    const existingItem = itemsMap.get(String(id));
    const item = {
      ...existingItem,
      value: id,
      label: name || existingItem?.label || `${fallback} #${id}`,
    };
    const cityId = getEntityCityId(entity);
    const streetId = getEntityStreetId(entity);

    if (Number(cityId) > 0) {
      item.cityId = cityId;
    }

    if (streetId !== undefined && streetId !== null) {
      item.streetId = streetId;
    }

    itemsMap.set(String(id), item);
  });

  selectedIds.forEach((id) => {
    if (id === undefined || id === null || itemsMap.has(String(id))) {
      return;
    }

    itemsMap.set(String(id), {
      value: id,
      label: `${fallback} #${id}`,
    });
  });

  return Array.from(itemsMap.values());
};

const getAddressIds = ({ entities = [], ids = [], idKeys }) => {
  const values = [
    ...getArrayValue(ids),
    ...getArrayValue(entities).map((entity) => getEntityId(entity, idKeys)),
  ];
  const valueSet = new Set();

  return values.filter((value) => {
    if (value === undefined || value === null || value === '') {
      return false;
    }

    const valueKey = String(value);

    if (valueSet.has(valueKey)) {
      return false;
    }

    valueSet.add(valueKey);
    return true;
  });
};

export const getScheduleAddressQueryParams = (schedule) => {
  const address = schedule?.address || {};

  return {
    companyId: schedule?.companyId || address.companyId,
    cityIds: getAddressIds({
      entities: [
        schedule?.cities,
        address.cities,
        schedule?.city,
        address.city,
      ].flatMap(getArrayValue),
      ids: [
        schedule?.cityIds,
        address.cityIds,
        schedule?.cityId,
        address.cityId,
      ].flatMap(getArrayValue),
      idKeys: ['cityId', 'id', 'value'],
    }),
    streetIds: getAddressIds({
      entities: [
        schedule?.streets,
        address.streets,
        schedule?.street,
        address.street,
      ].flatMap(getArrayValue),
      ids: [
        schedule?.streetIds,
        address.streetIds,
        schedule?.streetId,
        address.streetId,
      ].flatMap(getArrayValue),
      idKeys: ['streetId', 'id', 'value'],
    }),
  };
};

export const getScheduleAddressSummary = (schedule, directories = {}) => {
  const address = schedule?.address || {};
  const cityIds = [
    schedule?.cityIds,
    address.cityIds,
    schedule?.cityId,
    address.cityId,
  ].flatMap(getArrayValue);
  const cityRegionIds = [
    schedule?.cityRegionIds,
    address.cityRegionIds,
    schedule?.cityRegionId,
    address.cityRegionId,
  ].flatMap(getArrayValue);
  const streetIds = [
    schedule?.streetIds,
    address.streetIds,
    schedule?.streetId,
    address.streetId,
  ].flatMap(getArrayValue);
  const houseIds = [
    schedule?.houseIds,
    address.houseIds,
    schedule?.houseId,
    address.houseId,
  ].flatMap(getArrayValue);

  const cityItems = createAddressItems({
    entities: [
      cityIds.length ? directories.cities : [],
      schedule?.cities,
      address.cities,
      schedule?.city,
      address.city,
    ].flatMap(getArrayValue),
    ids: cityIds,
    idKeys: ['cityId', 'id', 'value'],
    fallback: 'Город',
  });
  const cityRegionItems = createAddressItems({
    entities: [
      cityRegionIds.length ? directories.cityRegions : [],
      schedule?.cityRegions,
      address.cityRegions,
      schedule?.cityRegion,
      address.cityRegion,
    ].flatMap(getArrayValue),
    ids: cityRegionIds,
    idKeys: ['cityRegionId', 'regionId', 'id', 'value'],
    fallback: 'Микрорайон',
  });
  const streetItems = createAddressItems({
    entities: [
      streetIds.length ? directories.streets : [],
      schedule?.streets,
      address.streets,
      schedule?.street,
      address.street,
    ].flatMap(getArrayValue),
    ids: streetIds,
    idKeys: ['streetId', 'id', 'value'],
    fallback: 'Улица',
  });
  const houseItems = createAddressItems({
    entities: [
      houseIds.length ? directories.houses : [],
      schedule?.houses,
      address.houses,
      schedule?.house,
      address.house,
    ].flatMap(getArrayValue),
    ids: houseIds,
    idKeys: ['houseId', 'id', 'value'],
    fallback: 'Дом',
    getName: getHouseName,
  });
  const allHouseItems = createAddressItems({
    entities: directories.houses || [],
    ids: [],
    idKeys: ['houseId', 'id', 'value'],
    fallback: 'Дом',
    getName: getHouseName,
  });
  const allCompanyAddresses = Boolean(
    schedule?.allCompanyAddresses || address.allCompanyAddresses
  );

  return {
    allCompanyAddresses,
    cityItems,
    cityRegionItems,
    streetItems,
    houseItems,
    allHouseItems,
  };
};

export const getPreviewKey = (prefix, value, index) =>
  `${prefix}-${value ?? index}`;
